import { ApolloLink } from "@apollo/client";
import { graphQLResultHasProtocolErrors, PROTOCOL_ERRORS_SYMBOL } from "@apollo/client/errors";
import { getMainDefinition, Observable } from "@apollo/client/utilities";
import { GraphQLFormattedError, Kind, OperationTypeNode } from "graphql";

/**
 * How a connection ended. The three shapes are distinct on the wire and carry
 * different amounts of information:
 *
 * - `complete` — the stream ended cleanly. Usually the server's staleness
 *   recycle. Carries nothing to classify, so the health heuristic decides.
 * - `errorFrame` — a GraphQL rejection delivered *in-band*: multipart sends it as
 *   a frame with no `data`, then completes. This is where a subgraph rejection
 *   lands, and the only shape that carries `extensions.code`. See `errorsOfFrame`
 *   for the two places those errors hide.
 * - `error` — a transport failure (offline, non-2xx, unparseable body).
 */
export type SubscriptionTerminal =
  | { kind: "complete" }
  | { kind: "errorFrame"; errors: readonly GraphQLFormattedError[] }
  | { kind: "error"; error: unknown };

/**
 * What to do about a terminal. `refreshCredentials` is separate from `retry`
 * because the two answers are independent: a transient network drop is worth
 * retrying with the credentials we already hold, and paying a token round-trip
 * before it only delays recovery.
 */
export type ReconnectDecision = { retry: true; refreshCredentials: boolean } | { retry: false };

export type ReconnectingSubscriptionLinkOptions = {
  /** Backoff before the first reconnect attempt, in ms. Doubles each consecutive attempt. */
  initialDelayMs?: number;
  /** Upper bound on the backoff delay, in ms. */
  maxDelayMs?: number;
  /**
   * How many *consecutive* fast, data-less reconnect attempts may occur before the
   * link gives up and surfaces the terminal signal to the consumer. A connection
   * that delivers data — or that stays alive at least `minHealthyDurationMs` — resets
   * the counter, so a long-lived stream the server periodically recycles (its
   * staleness teardown), including an idle event-stream with no frames between
   * recycles, reconnects indefinitely; only a stream that keeps terminating
   * immediately (e.g. a permanently-rejected subscription) is abandoned.
   */
  maxConsecutiveAttempts?: number;
  /**
   * Minimum time (ms) a connection must stay alive to count as healthy even if it
   * delivered no data — this is what distinguishes a real connection the server
   * later recycled (staleness) from one it rejected immediately. Defaults to 30000.
   * See `onTerminal` for what the clock runs from.
   */
  minHealthyDurationMs?: number;
  /**
   * Decides what to do about each terminal. Defaults to retrying everything and
   * always refreshing credentials, which is the behaviour this link shipped with.
   */
  classifyTerminal?: (
    terminal: SubscriptionTerminal,
    context: { consecutiveAttempts: number; wasHealthy: boolean },
  ) => ReconnectDecision;
  /**
   * Called before a reconnect attempt whose decision asked for it, and awaited
   * before re-forwarding. Use it to refresh credentials so the reconnect carries a
   * fresh token: the subgraph rejects a re-subscribe whose token is inside its
   * staleness window, and the auth header link reads a *cached* token by default, so
   * without a forced refresh every staleness-triggered reconnect would re-send the
   * same soon-to-expire token and be rejected until the cap gives up. If it throws,
   * the reconnect proceeds anyway (best-effort).
   */
  onReconnect?: () => void | Promise<void>;
};

/** Retry everything, always refreshing — the behaviour before classification existed. */
const retryEverything = (): ReconnectDecision => ({ retry: true, refreshCredentials: true });

/**
 * The errors carried by a data-less frame, from either shape the transport uses.
 *
 * A rejection raised inside the operation arrives as ordinary `result.errors`.
 * A *fatal* one arrives at the multipart envelope level instead
 * (`{"payload": null, "errors": [...]}`), and Apollo does not surface those as
 * `result.errors` — it hides them under a symbol in `extensions` for the layer
 * above this link to convert into `result.error`. Both mean the same thing here,
 * and the envelope shape is the one an unauthorised subscriber actually gets.
 */
const errorsOfFrame = (result: ApolloLink.Result): readonly GraphQLFormattedError[] | undefined => {
  if (graphQLResultHasProtocolErrors(result)) {
    return result.extensions[PROTOCOL_ERRORS_SYMBOL].errors;
  }

  return result.errors?.length ? result.errors : undefined;
};

const isSubscriptionOperation = (query: ApolloLink.Operation["query"]): boolean => {
  const definition = getMainDefinition(query);
  return (
    definition.kind === Kind.OPERATION_DEFINITION &&
    definition.operation === OperationTypeNode.SUBSCRIPTION
  );
};

/**
 * Apollo link that transparently reconnects GraphQL subscriptions.
 *
 * The supergraph router serves subscriptions over HTTP multipart (no graphql-ws
 * client), and the subgraph tears a stream down cleanly when the viewer's token
 * nears expiry (its "staleness" teardown). Apollo's `useSubscription` treats that
 * clean `complete` as terminal and does not restart it, and the link chain has no
 * reconnect of its own — so without this link every subscription silently stops
 * updating after a teardown until its component remounts.
 *
 * This link sits in the chain and re-subscribes on BOTH `complete` and `error`
 * (Apollo's RetryLink only retries errors, so it would miss the clean staleness
 * `complete`), with exponential backoff + jitter. Only subscription operations are
 * wrapped; queries and mutations pass straight through untouched. The consumer
 * unsubscribing (component unmount, `skip` flipping true, or a variables change)
 * stops it for good.
 *
 * Two gates decide whether a terminal is retried: `classifyTerminal` judges what
 * the terminal carries, and the health heuristic + attempt cap backstop the ones
 * that carry nothing to judge — a clean `complete` says only that the stream ended.
 *
 * Assumes subscriptions are long-lived streams (the convention in this app). The
 * failure cap counts only consecutive *fast, data-less* terminals, so a healthy
 * idle stream the server recycles reconnects indefinitely while a subscription that
 * keeps terminating immediately is abandoned; a (hypothetical) deliberately-finite
 * subscription that runs and then completes would be re-opened — this app has none.
 */
export const reconnectingSubscriptionLink = (
  options: ReconnectingSubscriptionLinkOptions = {},
): ApolloLink => {
  const initialDelayMs = options.initialDelayMs ?? 1_000;
  const maxDelayMs = options.maxDelayMs ?? 30_000;
  const maxConsecutiveAttempts = options.maxConsecutiveAttempts ?? 10;
  const minHealthyDurationMs = options.minHealthyDurationMs ?? 30_000;
  const classifyTerminal = options.classifyTerminal ?? retryEverything;
  const onReconnect = options.onReconnect;

  return new ApolloLink((operation, forward) => {
    if (!isSubscriptionOperation(operation.query)) {
      return forward(operation);
    }

    return new Observable<ApolloLink.Result>((subscriber) => {
      let innerSubscription: { unsubscribe(): void } | null = null;
      let retryTimer: ReturnType<typeof setTimeout> | undefined;
      let consecutiveAttempts = 0;
      let connectedAtMs = 0;
      let respondedAtMs: number | undefined;
      let deliveredData = false;
      let stopped = false;

      const reconnectDelay = (): number => {
        // A healthy recycle (counter reset to 0) reconnects immediately: the
        // live-only fanout does not replay, so any event during the gap is lost —
        // keep it minimal (a credential refresh, when the decision asked for one,
        // still runs first). Backoff with jitter applies only to fast failed
        // attempts, where it prevents hammering / a thundering-herd.
        if (consecutiveAttempts === 0) {
          return 0;
        }
        const exponential = initialDelayMs * 2 ** (consecutiveAttempts - 1);
        const capped = Math.min(exponential, maxDelayMs);
        // Equal jitter ([capped/2, capped]) de-correlates failed reconnects.
        return Math.round(capped / 2 + Math.random() * (capped / 2));
      };

      const onTerminal = (terminal: SubscriptionTerminal, giveUp: () => void): void => {
        if (stopped) {
          return;
        }
        innerSubscription?.unsubscribe();
        innerSubscription = null;

        // Healthy = delivered data, or stayed alive past the threshold: the server
        // accepted it and later recycled it. Only a FAST, data-less terminal counts
        // as a failed attempt, so an idle event-stream reconnects indefinitely while
        // an immediate-rejection loop is still capped.
        //
        // The clock runs from the first frame, falling back to the request. An
        // in-band rejection IS that first frame, so a slow one stays unhealthy
        // instead of clearing the threshold and looping forever at zero backoff; an
        // idle stream has no frame, so the request clock is what lets it ever count
        // as healthy at all.
        const healthy =
          deliveredData || Date.now() - (respondedAtMs ?? connectedAtMs) >= minHealthyDurationMs;

        const decision = classifyTerminal(terminal, {
          consecutiveAttempts,
          wasHealthy: healthy,
        });
        if (!decision.retry) {
          stopped = true;
          giveUp();
          return;
        }

        if (healthy) {
          consecutiveAttempts = 0;
        } else {
          consecutiveAttempts += 1;
          if (consecutiveAttempts > maxConsecutiveAttempts) {
            stopped = true;
            giveUp();
            return;
          }
        }
        retryTimer = setTimeout(() => {
          void reconnect(decision.refreshCredentials);
        }, reconnectDelay());
      };

      // Refreshes only when the decision asked for it — awaiting a token round-trip
      // on a transient drop delays recovery on exactly the failure where the network
      // is the thing that is broken.
      const reconnect = async (refreshCredentials: boolean): Promise<void> => {
        retryTimer = undefined;
        if (stopped) {
          return;
        }
        if (refreshCredentials && onReconnect) {
          try {
            await onReconnect();
          } catch {
            // Best-effort: reconnect with whatever credentials we already have.
          }
          // The consumer may have unsubscribed while onReconnect was awaited.
          if (stopped) {
            return;
          }
        }
        connect();
      };

      function connect(): void {
        if (stopped) {
          return;
        }
        connectedAtMs = Date.now();
        respondedAtMs = undefined;
        deliveredData = false;
        // The errors of the last data-less frame, so the terminal that follows can
        // be classified by what the server actually said. HTTP-multipart delivers a
        // rejection as `next` + `complete`, so the `complete` handler alone cannot
        // tell a rejection from a routine recycle.
        let lastErrors: readonly GraphQLFormattedError[] | undefined;
        innerSubscription = forward(operation).subscribe({
          next: (result) => {
            respondedAtMs ??= Date.now();
            // Track whether this connection delivered real data. HTTP-multipart
            // delivers a GraphQL rejection in-band via `next` (with no data), so
            // an error frame must NOT mark the connection healthy.
            const errors = errorsOfFrame(result);
            if (errors) {
              lastErrors = errors;
            } else if (result.data != null) {
              deliveredData = true;
              lastErrors = undefined;
            }
            subscriber.next(result);
          },
          // A clean complete is usually the server recycling the stream (staleness),
          // not the end of it — reconnect rather than completing the consumer's
          // subscription (unless the policy or the cap says otherwise; see onTerminal).
          complete: () =>
            onTerminal(
              lastErrors ? { kind: "errorFrame", errors: lastErrors } : { kind: "complete" },
              () => subscriber.complete(),
            ),
          error: (error) => onTerminal({ kind: "error", error }, () => subscriber.error(error)),
        });
      }

      connect();

      return () => {
        // Consumer unsubscribed (unmount / skip / variables change): stop for good.
        stopped = true;
        if (retryTimer !== undefined) {
          clearTimeout(retryTimer);
          retryTimer = undefined;
        }
        innerSubscription?.unsubscribe();
      };
    });
  });
};
